import React, { useState, useEffect, useRef } from 'react';
import { 
  Car, Zap, Settings, ChevronRight, Loader2, 
  Send, CheckCircle, Fuel, Gauge, ArrowRight
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = '', prefix = '', duration = 1500 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startTimeRef = useRef(null);
  
  useEffect(() => {
    if (!value) return;
    
    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.round(value * easeOutQuart));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    startTimeRef.current = null;
    requestAnimationFrame(animate);
  }, [value, duration]);
  
  return (
    <span className="tabular-nums">
      {prefix}{displayValue.toLocaleString('de-CH')}{suffix}
    </span>
  );
};

// Power Chart Component
const PowerChart = ({ data, stage, branding }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [animationProgress, setAnimationProgress] = useState(0);
  
  useEffect(() => {
    // Reset animation when stage changes
    setAnimationProgress(0);
  }, [stage, data]);
  
  useEffect(() => {
    if (!data || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Chart margins for axis labels
    const marginLeft = 55;
    const marginRight = 20;
    const marginTop = 20;
    const marginBottom = 45;
    const chartWidth = width - marginLeft - marginRight;
    const chartHeight = height - marginTop - marginBottom;
    
    const rpm = data.rpm || [];
    const originalHp = data.original_hp || [];
    const stage1Hp = data.stage1_hp || [];
    const stage2Hp = data.stage2_hp || [];
    
    if (rpm.length === 0) return;
    
    // Find max values for scaling
    const allHp = [...originalHp, ...stage1Hp, ...stage2Hp];
    const maxHp = Math.ceil(Math.max(...allHp) / 50) * 50 + 25; // Round up to nearest 50
    const minHp = 0;
    const maxRpm = Math.max(...rpm);
    const minRpm = Math.min(...rpm);
    
    // Animation function
    const animate = () => {
      if (animationProgress < 1) {
        setAnimationProgress(prev => Math.min(prev + 0.03, 1));
      }
    };
    
    // Start animation
    if (animationProgress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    }
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Helper: Convert data to canvas coordinates
    const toX = (rpmVal) => marginLeft + ((rpmVal - minRpm) / (maxRpm - minRpm)) * chartWidth;
    const toY = (hpVal) => marginTop + chartHeight - ((hpVal - minHp) / (maxHp - minHp)) * chartHeight;
    
    // ===== DRAW BACKGROUND =====
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(marginLeft, marginTop, chartWidth, chartHeight);
    
    // ===== DRAW GRID =====
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines (HP)
    const hpSteps = 5;
    for (let i = 0; i <= hpSteps; i++) {
      const hpVal = minHp + (maxHp - minHp) * (i / hpSteps);
      const y = toY(hpVal);
      ctx.beginPath();
      ctx.moveTo(marginLeft, y);
      ctx.lineTo(width - marginRight, y);
      ctx.stroke();
    }
    
    // Vertical grid lines (RPM)
    const rpmSteps = 5;
    for (let i = 0; i <= rpmSteps; i++) {
      const rpmVal = minRpm + (maxRpm - minRpm) * (i / rpmSteps);
      const x = toX(rpmVal);
      ctx.beginPath();
      ctx.moveTo(x, marginTop);
      ctx.lineTo(x, marginTop + chartHeight);
      ctx.stroke();
    }
    
    // ===== DRAW AXES =====
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 2;
    
    // Y-Axis (left)
    ctx.beginPath();
    ctx.moveTo(marginLeft, marginTop);
    ctx.lineTo(marginLeft, marginTop + chartHeight);
    ctx.stroke();
    
    // X-Axis (bottom)
    ctx.beginPath();
    ctx.moveTo(marginLeft, marginTop + chartHeight);
    ctx.lineTo(width - marginRight, marginTop + chartHeight);
    ctx.stroke();
    
    // ===== DRAW AXIS LABELS =====
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '11px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    
    // Y-Axis labels (HP)
    for (let i = 0; i <= hpSteps; i++) {
      const hpVal = Math.round(minHp + (maxHp - minHp) * (i / hpSteps));
      const y = toY(hpVal);
      ctx.fillText(`${hpVal}`, marginLeft - 8, y);
    }
    
    // Y-Axis title
    ctx.save();
    ctx.translate(14, marginTop + chartHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.font = 'bold 12px Inter, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillText('PS', 0, 0);
    ctx.restore();
    
    // X-Axis labels (RPM)
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = '10px Inter, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    for (let i = 0; i <= rpmSteps; i++) {
      const rpmVal = Math.round((minRpm + (maxRpm - minRpm) * (i / rpmSteps)) / 100) * 100;
      const x = toX(rpmVal);
      ctx.fillText(`${(rpmVal / 1000).toFixed(1)}k`, x, marginTop + chartHeight + 8);
    }
    
    // X-Axis title
    ctx.font = 'bold 12px Inter, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillText('U/min', marginLeft + chartWidth / 2, height - 8);
    
    // ===== DRAW CURVES WITH ANIMATION =====
    const drawAnimatedCurve = (points, color, fillColor, lineWidth = 3, showFill = true) => {
      if (points.length === 0) return;
      
      const animatedPointCount = Math.floor(points.length * animationProgress);
      if (animatedPointCount < 2) return;
      
      // Create smooth curve using bezier
      ctx.beginPath();
      ctx.moveTo(toX(rpm[0]), toY(points[0]));
      
      for (let i = 1; i < animatedPointCount; i++) {
        const xc = (toX(rpm[i]) + toX(rpm[i - 1])) / 2;
        const yc = (toY(points[i]) + toY(points[i - 1])) / 2;
        ctx.quadraticCurveTo(toX(rpm[i - 1]), toY(points[i - 1]), xc, yc);
      }
      
      // Draw to last point
      const lastIdx = animatedPointCount - 1;
      ctx.lineTo(toX(rpm[lastIdx]), toY(points[lastIdx]));
      
      // Fill gradient under curve
      if (showFill && animationProgress > 0.1) {
        const gradient = ctx.createLinearGradient(0, marginTop, 0, marginTop + chartHeight);
        gradient.addColorStop(0, fillColor);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        
        ctx.lineTo(toX(rpm[lastIdx]), marginTop + chartHeight);
        ctx.lineTo(toX(rpm[0]), marginTop + chartHeight);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Redraw the line on top
        ctx.beginPath();
        ctx.moveTo(toX(rpm[0]), toY(points[0]));
        for (let i = 1; i < animatedPointCount; i++) {
          const xc = (toX(rpm[i]) + toX(rpm[i - 1])) / 2;
          const yc = (toY(points[i]) + toY(points[i - 1])) / 2;
          ctx.quadraticCurveTo(toX(rpm[i - 1]), toY(points[i - 1]), xc, yc);
        }
        ctx.lineTo(toX(rpm[lastIdx]), toY(points[lastIdx]));
      }
      
      // Stroke the curve with glow effect
      ctx.shadowColor = color;
      ctx.shadowBlur = 8;
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.shadowBlur = 0;
      
      // Draw point at end of animated line
      if (animationProgress < 1 && animatedPointCount > 0) {
        ctx.beginPath();
        ctx.arc(toX(rpm[lastIdx]), toY(points[lastIdx]), 5, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    };
    
    // Draw curves in order (back to front)
    // Original (gray, subtle)
    drawAnimatedCurve(originalHp, 'rgba(148, 163, 184, 0.6)', 'rgba(148, 163, 184, 0.1)', 2, false);
    
    // Stage 1 (green)
    if (stage === 'stage1' || stage === 'stage2') {
      drawAnimatedCurve(stage1Hp, '#22C55E', 'rgba(34, 197, 94, 0.2)', 3, stage === 'stage1');
    }
    
    // Stage 2 (orange/amber)
    if (stage === 'stage2') {
      drawAnimatedCurve(stage2Hp, '#F59E0B', 'rgba(245, 158, 11, 0.25)', 3.5, true);
    }
    
    // ===== DRAW LEGEND =====
    const legendY = marginTop + 12;
    const legendSpacing = 90;
    let legendX = marginLeft + 10;
    
    const drawLegendItem = (label, color, x) => {
      // Line sample
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.moveTo(x, legendY);
      ctx.lineTo(x + 20, legendY);
      ctx.stroke();
      
      // Label
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = '11px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x + 26, legendY);
    };
    
    drawLegendItem('Original', 'rgba(148, 163, 184, 0.6)', legendX);
    legendX += legendSpacing;
    
    if (stage === 'stage1' || stage === 'stage2') {
      drawLegendItem('Stage 1', '#22C55E', legendX);
      legendX += legendSpacing;
    }
    
    if (stage === 'stage2') {
      drawLegendItem('Stage 2', '#F59E0B', legendX);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    
  }, [data, stage, animationProgress, branding]);
  
  return (
    <canvas 
      ref={canvasRef} 
      width={500} 
      height={280}
      className="w-full h-auto rounded-lg"
      style={{ maxHeight: '280px' }}
    />
  );
};

const TuningConfiguratorEmbed = () => {
  const [searchParams] = useSearchParams();
  const apiKey = searchParams.get('key');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [branding, setBranding] = useState({});
  
  // Selection state - NEW ORDER: Brand -> Model -> Year -> Engine
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [engines, setEngines] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedEngine, setSelectedEngine] = useState('');
  
  // Vehicle data
  const [vehicleData, setVehicleData] = useState(null);
  const [vehicleImageUrl, setVehicleImageUrl] = useState(null);
  const [selectedStage, setSelectedStage] = useState('stage1');
  const [selectedOptions, setSelectedOptions] = useState([]);
  
  // Inquiry form
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Load config - use XMLHttpRequest to avoid fetch Response body reuse issues
  useEffect(() => {
    if (!apiKey) {
      setError('Kein API-Key angegeben');
      setLoading(false);
      return;
    }
    
    let cancelled = false;
    
    // Use XMLHttpRequest to avoid Response body caching issues with fetch
    const makeRequest = (url) => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Cache-Control', 'no-cache, no-store');
        xhr.setRequestHeader('Pragma', 'no-cache');
        
        xhr.onload = function() {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            data: xhr.responseText
          });
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        
        xhr.send();
      });
    };
    
    const loadConfig = async () => {
      try {
        const timestamp = Date.now();
        
        // Fetch config
        const configResult = await makeRequest(
          `${BACKEND_URL}/api/embed/tuning/${apiKey}/config?_t=${timestamp}`
        );
        
        if (cancelled) return;
        
        if (!configResult.ok) {
          let errorDetail = configResult.data;
          try {
            const errorJson = JSON.parse(configResult.data);
            errorDetail = errorJson.detail || configResult.data;
          } catch (e) {
            // Not JSON
          }
          
          if (configResult.status === 401) {
            throw new Error('Ungültiger API-Key. Bitte prüfen Sie den Konfigurator-Link.');
          } else if (configResult.status === 403) {
            throw new Error('Der Konfigurator ist nicht aktiviert.');
          }
          throw new Error(errorDetail || 'Konfiguration konnte nicht geladen werden');
        }
        
        const configData = JSON.parse(configResult.data);
        
        // Fetch brands
        const brandsResult = await makeRequest(
          `${BACKEND_URL}/api/embed/tuning/${apiKey}/brands?_t=${timestamp}`
        );
        
        if (cancelled) return;
        
        if (!brandsResult.ok) {
          throw new Error('Marken konnten nicht geladen werden');
        }
        
        const brandsData = JSON.parse(brandsResult.data);
        
        if (!cancelled) {
          setConfig(configData);
          setBranding(configData.branding || {});
          setBrands(brandsData.brands || []);
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Config load error:', err);
        setError(err.message || 'Ein Fehler ist aufgetreten');
        setLoading(false);
      }
    };
    
    loadConfig();
    
    return () => {
      cancelled = true;
    };
  }, [apiKey]);
  
  const loadModels = async (brand) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/embed/tuning/${apiKey}/brands/${encodeURIComponent(brand)}/models`);
      const data = await response.json();
      setModels(data.models || []);
      setYears([]);
      setEngines([]);
      setSelectedModel('');
      setSelectedYear('');
      setSelectedEngine('');
      setVehicleData(null);
      setVehicleImageUrl(null);
    } catch (err) {
      console.error('Failed to load models:', err);
    }
  };
  
  const loadYears = async (brand, model) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/embed/tuning/${apiKey}/brands/${encodeURIComponent(brand)}/models/${encodeURIComponent(model)}/years`);
      const data = await response.json();
      setYears(data.years || []);
      setEngines([]);
      setSelectedYear('');
      setSelectedEngine('');
      setVehicleData(null);
      // Load vehicle image when model is selected
      loadVehicleImage(brand, model);
    } catch (err) {
      console.error('Failed to load years:', err);
    }
  };
  
  const loadEngines = async (brand, model, year) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/embed/tuning/${apiKey}/brands/${encodeURIComponent(brand)}/models/${encodeURIComponent(model)}/years/${year}/engines`);
      const data = await response.json();
      setEngines(data.engines || []);
      setSelectedEngine('');
      setVehicleData(null);
    } catch (err) {
      console.error('Failed to load engines:', err);
    }
  };
  
  const loadVehicleImage = async (brand, model) => {
    try {
      // Use the public embed endpoint for vehicle images
      const response = await fetch(
        `${BACKEND_URL}/api/embed/tuning/${apiKey}/vehicle-image/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setVehicleImageUrl(url);
          return;
        }
      }
      setVehicleImageUrl(null);
    } catch (err) {
      console.error('Failed to load vehicle image:', err);
      setVehicleImageUrl(null);
    }
  };
  
  const loadVehicleData = async (brand, model, engine) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/embed/tuning/${apiKey}/vehicle/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/${encodeURIComponent(engine)}`);
      const data = await response.json();
      setVehicleData(data);
      setSelectedOptions([]);
    } catch (err) {
      console.error('Failed to load vehicle data:', err);
    }
  };
  
  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    if (brand) loadModels(brand);
  };
  
  const handleModelChange = (model) => {
    setSelectedModel(model);
    if (model && selectedBrand) loadYears(selectedBrand, model);
  };
  
  const handleYearChange = (year) => {
    setSelectedYear(year);
    if (year && selectedBrand && selectedModel) {
      loadEngines(selectedBrand, selectedModel, year);
    }
  };
  
  const handleEngineChange = (engine) => {
    setSelectedEngine(engine);
    if (engine && selectedBrand && selectedModel) {
      loadVehicleData(selectedBrand, selectedModel, engine);
    }
  };
  
  const toggleOption = (optionId) => {
    setSelectedOptions(prev => 
      prev.includes(optionId) 
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };
  
  const calculateTotalPrice = () => {
    if (!vehicleData || !config?.show_prices) return null;
    
    let total = 0;
    
    if (selectedStage === 'stage1' && vehicleData.stage1?.price_avg) {
      total = vehicleData.stage1.price_avg;
    } else if (selectedStage === 'stage2' && vehicleData.stage2?.price_avg) {
      total = vehicleData.stage2.price_avg;
    }
    
    selectedOptions.forEach(optId => {
      const opt = vehicleData.options?.find(o => o.id === optId);
      if (opt?.price_avg) total += opt.price_avg;
    });
    
    return total;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/embed/tuning/${apiKey}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicle: {
            brand: selectedBrand,
            model: selectedModel,
            year: selectedYear,
            engine: selectedEngine
          },
          selected_stage: selectedStage,
          selected_options: selectedOptions,
          customer: formData
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Fehler beim Senden');
      }
    } catch (err) {
      alert('Fehler beim Senden der Anfrage');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: branding.background_color || '#151C28' }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: branding.primary_color || '#1EA0E6' }} />
      </div>
    );
  }
  
  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: branding.background_color || '#151C28' }}
      >
        <div className="text-center">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }
  
  const primaryColor = branding.primary_color || '#1EA0E6';
  const accentColor = branding.accent_color || '#7CD4FD';
  const bgColor = branding.background_color || '#151C28';
  const textColor = branding.text_color || '#FFFFFF';
  
  return (
    <div 
      className="min-h-screen p-4 md:p-6"
      style={{ 
        backgroundColor: bgColor,
        color: textColor,
        fontFamily: branding.font_family || 'Inter, sans-serif'
      }}
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          {/* User Logo (if uploaded) */}
          {branding.logo_url && (
            <div className="mb-4 flex justify-center">
              <img 
                src={branding.logo_url} 
                alt={config?.company_name || 'Logo'} 
                className="max-h-16 max-w-[200px] object-contain"
              />
            </div>
          )}
          <h1 className="text-2xl md:text-3xl font-bold flex items-center justify-center gap-3">
            {!branding.logo_url && <Zap className="w-8 h-8" style={{ color: primaryColor }} />}
            Tuning Konfigurator
          </h1>
          {config?.company_name && (
            <p className="text-sm opacity-60 mt-1">{config.company_name}</p>
          )}
        </div>
        
        {/* Vehicle Selection */}
        <div 
          className="p-4 md:p-6 rounded-2xl"
          style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Car className="w-5 h-5" style={{ color: accentColor }} />
            Fahrzeug auswählen
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Brand Select */}
            <div>
              <label className="block text-sm opacity-60 mb-1">Hersteller</label>
              <select
                value={selectedBrand}
                onChange={(e) => handleBrandChange(e.target.value)}
                className="w-full p-3 rounded-xl border border-white/10 text-white focus:outline-none focus:border-opacity-50"
                style={{ 
                  borderColor: `${primaryColor}40`,
                  backgroundColor: '#1a1a2e',
                  colorScheme: 'dark'
                }}
              >
                <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>Hersteller wählen...</option>
                {brands.map(brand => (
                  <option key={brand} value={brand} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>{brand}</option>
                ))}
              </select>
            </div>
            
            {/* Model Select */}
            <div>
              <label className="block text-sm opacity-60 mb-1">Modell</label>
              <select
                value={selectedModel}
                onChange={(e) => handleModelChange(e.target.value)}
                disabled={!selectedBrand}
                className="w-full p-3 rounded-xl border border-white/10 text-white focus:outline-none disabled:opacity-50"
                style={{ 
                  borderColor: `${primaryColor}40`,
                  backgroundColor: '#1a1a2e',
                  colorScheme: 'dark'
                }}
              >
                <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>Modell wählen...</option>
                {models.map(model => (
                  <option key={model.name || model} value={model.name || model} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                    {model.name || model} {model.year_start && model.year_end ? `(${model.year_start}-${model.year_end})` : ''}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Year Select - NEW */}
            <div>
              <label className="block text-sm opacity-60 mb-1">Baujahr</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                disabled={!selectedModel}
                className="w-full p-3 rounded-xl border border-white/10 text-white focus:outline-none disabled:opacity-50"
                style={{ 
                  borderColor: `${primaryColor}40`,
                  backgroundColor: '#1a1a2e',
                  colorScheme: 'dark'
                }}
              >
                <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>Baujahr wählen...</option>
                {years.map(year => (
                  <option key={year} value={year} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>{year}</option>
                ))}
              </select>
            </div>
            
            {/* Engine Select */}
            <div>
              <label className="block text-sm opacity-60 mb-1">Motor</label>
              <select
                value={selectedEngine}
                onChange={(e) => handleEngineChange(e.target.value)}
                disabled={!selectedYear}
                className="w-full p-3 rounded-xl border border-white/10 text-white focus:outline-none disabled:opacity-50"
                style={{ 
                  borderColor: `${primaryColor}40`,
                  backgroundColor: '#1a1a2e',
                  colorScheme: 'dark'
                }}
              >
                <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>Motor wählen...</option>
                {engines.map(eng => (
                  <option key={eng.engine_name} value={eng.engine_name} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                    {eng.engine_name} {eng.original?.power_ps ? `(${eng.original.power_ps} PS)` : ''} {eng.fuel_type === 'diesel' ? '- Diesel' : eng.fuel_type === 'petrol' ? '- Benzin' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Vehicle Image Display */}
          {vehicleImageUrl && selectedModel && (
            <div className="mt-6 flex justify-center">
              <div 
                className="relative rounded-2xl overflow-hidden p-4"
                style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
              >
                <img 
                  src={vehicleImageUrl} 
                  alt={`${selectedBrand} ${selectedModel}`}
                  className="max-h-48 w-auto object-contain"
                />
                <div className="absolute bottom-2 left-2 right-2 text-center">
                  <p className="text-sm font-semibold" style={{ color: primaryColor }}>
                    {selectedBrand} {selectedModel} {selectedYear && `(${selectedYear})`}
                  </p>
                  {selectedEngine && (
                    <p className="text-xs opacity-60">{selectedEngine}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Results */}
        {vehicleData && (
          <>
            {/* Power Display */}
            <div 
              className="p-4 md:p-6 rounded-2xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Gauge className="w-5 h-5" style={{ color: accentColor }} />
                Leistungssteigerung
              </h2>
              
              {/* Stage Selection */}
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setSelectedStage('stage1')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    selectedStage === 'stage1' ? 'border-opacity-100' : 'border-opacity-20 opacity-60'
                  }`}
                  style={{ 
                    borderColor: selectedStage === 'stage1' ? '#22C55E' : 'rgba(255,255,255,0.2)',
                    backgroundColor: selectedStage === 'stage1' ? 'rgba(34,197,94,0.1)' : 'transparent'
                  }}
                >
                  <div className="text-sm opacity-60">Stage 1</div>
                  <div className="text-2xl font-bold" style={{ color: '#22C55E' }}>
                    <AnimatedCounter value={vehicleData.stage1?.power_ps} suffix=" PS" />
                  </div>
                </button>
                
                <button
                  onClick={() => setSelectedStage('stage2')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    selectedStage === 'stage2' ? 'border-opacity-100' : 'border-opacity-20 opacity-60'
                  }`}
                  style={{ 
                    borderColor: selectedStage === 'stage2' ? '#F59E0B' : 'rgba(255,255,255,0.2)',
                    backgroundColor: selectedStage === 'stage2' ? 'rgba(245,158,11,0.1)' : 'transparent'
                  }}
                >
                  <div className="text-sm opacity-60">Stage 2</div>
                  <div className="text-2xl font-bold" style={{ color: '#F59E0B' }}>
                    <AnimatedCounter value={vehicleData.stage2?.power_ps} suffix=" PS" />
                  </div>
                </button>
              </div>
              
              {/* Power Comparison */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <div className="text-sm opacity-60">Original</div>
                  <div className="text-xl font-bold">
                    {vehicleData.original?.power_ps} PS
                  </div>
                  <div className="text-sm opacity-60">
                    {vehicleData.original?.torque_nm} Nm
                  </div>
                </div>
                
                <div className="flex items-center justify-center">
                  <ArrowRight className="w-8 h-8 opacity-40" />
                </div>
                
                <div 
                  className="text-center p-4 rounded-xl"
                  style={{ 
                    backgroundColor: selectedStage === 'stage1' ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)',
                    border: `1px solid ${selectedStage === 'stage1' ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.3)'}`
                  }}
                >
                  <div className="text-sm opacity-60">{selectedStage === 'stage1' ? 'Stage 1' : 'Stage 2'}</div>
                  <div 
                    className="text-xl font-bold"
                    style={{ color: selectedStage === 'stage1' ? '#22C55E' : '#F59E0B' }}
                  >
                    <AnimatedCounter 
                      value={selectedStage === 'stage1' ? vehicleData.stage1?.power_ps : vehicleData.stage2?.power_ps} 
                      suffix=" PS" 
                    />
                  </div>
                  <div className="text-sm" style={{ color: selectedStage === 'stage1' ? '#22C55E' : '#F59E0B' }}>
                    +<AnimatedCounter 
                      value={selectedStage === 'stage1' ? vehicleData.stage1?.gains_ps : vehicleData.stage2?.gains_ps} 
                    /> PS
                  </div>
                </div>
              </div>
              
              {/* Power Chart */}
              {vehicleData.power_curve && (
                <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
                  <PowerChart data={vehicleData.power_curve} stage={selectedStage} branding={branding} />
                </div>
              )}
            </div>
            
            {/* Options */}
            {vehicleData.options?.filter(o => o.available).length > 0 && (
              <div 
                className="p-4 md:p-6 rounded-2xl"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5" style={{ color: accentColor }} />
                  Zusätzliche Optionen
                </h2>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {vehicleData.options.filter(o => o.available).map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => toggleOption(opt.id)}
                      className={`p-3 rounded-xl text-left transition-all ${
                        selectedOptions.includes(opt.id) ? 'ring-2' : ''
                      }`}
                      style={{ 
                        backgroundColor: selectedOptions.includes(opt.id) 
                          ? `${primaryColor}20` 
                          : 'rgba(255,255,255,0.05)',
                        borderColor: selectedOptions.includes(opt.id) ? primaryColor : 'transparent',
                        ringColor: primaryColor
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{opt.name}</span>
                        {selectedOptions.includes(opt.id) && (
                          <CheckCircle className="w-4 h-4" style={{ color: primaryColor }} />
                        )}
                      </div>
                      {config?.show_prices && opt.price_avg && (
                        <span className="text-xs opacity-60">+CHF {opt.price_avg}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Price & CTA */}
            <div 
              className="p-4 md:p-6 rounded-2xl"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor}20, ${accentColor}10)`,
                border: `1px solid ${primaryColor}40`
              }}
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">Ihre Auswahl</h3>
                  <p className="text-sm opacity-60">
                    {selectedBrand} {selectedModel} - {selectedStage.toUpperCase()}
                    {selectedOptions.length > 0 && ` + ${selectedOptions.length} Optionen`}
                  </p>
                  {config?.show_prices && calculateTotalPrice() && (
                    <div className="mt-2">
                      <span className="text-sm opacity-60">Geschätzter Preis: </span>
                      <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                        CHF <AnimatedCounter value={calculateTotalPrice()} />
                      </span>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => setShowForm(true)}
                  className="px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-transform hover:scale-105"
                  style={{ backgroundColor: primaryColor, color: '#fff' }}
                >
                  Preis anfragen
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
        
        {/* Inquiry Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <div 
              className="w-full max-w-md p-6 rounded-2xl"
              style={{ backgroundColor: bgColor, border: `1px solid ${primaryColor}40` }}
            >
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#22C55E' }} />
                  <h3 className="text-xl font-bold mb-2">Anfrage gesendet!</h3>
                  <p className="opacity-60">Wir melden uns in Kürze bei Ihnen.</p>
                  <button
                    onClick={() => { setShowForm(false); setSubmitted(false); }}
                    className="mt-6 px-6 py-2 rounded-lg"
                    style={{ backgroundColor: primaryColor, color: '#fff' }}
                  >
                    Schließen
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h3 className="text-xl font-bold mb-4">Preisanfrage senden</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm opacity-60 mb-1">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full p-3 rounded-xl bg-black/20 border border-white/10 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm opacity-60 mb-1">E-Mail *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full p-3 rounded-xl bg-black/20 border border-white/10 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm opacity-60 mb-1">Telefon</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full p-3 rounded-xl bg-black/20 border border-white/10 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm opacity-60 mb-1">Nachricht</label>
                      <textarea
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        className="w-full p-3 rounded-xl bg-black/20 border border-white/10 text-white resize-none"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 py-3 rounded-xl border border-white/20"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                      style={{ backgroundColor: primaryColor, color: '#fff' }}
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Senden
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TuningConfiguratorEmbed;
